
/**
 * Add tags to the active span if one exists.
 * @param tags
 */
export function addTagsToActiveSpan(tags: DatadogTag) {
  const span = tracer.scope().active();
  if (span) {
    span.addTags(tags);
  }
}

/**
 * Async wrapper function for tracer.trace that adds a default service tag, captures thrown errors, and ensures done is called.
 * @param name - The name of the span/resource/operation.
 * @param fn - The function to trace.
 * @returns The result of the function.
 */
export const traceAsync = async <T>(name: string, fn: DatadogTraceFn<Promise<T>>, options?: DatadogTraceOptions): Promise<T> => {
  return tracer.trace(name, {...defaultTraceOptions, ...options}, async (span, done) => {
    try {
      const result = await fn(span, done);
      done();
      return result;
    } catch (e) {
      done(e instanceof Error ? e : new Error(String(e)));
      throw e;
    }
  });
}

/**
 * Wrapper function for tracer.trace that adds a default service tag and captures thrown errors.
 * For async functions, use `traceAsync` instead.
 * 
 * @param name - The name of the span/resource/operation.
 * @param fn - The function to trace.
 * @returns The result of the function.
 */
export const trace = <T>(name: string, fn: DatadogTraceFn<T>, options?: DatadogTraceOptions): T =>  tracer.trace(name, {...defaultTraceOptions, ...options}, fn);


/**
 * Wrapper function for sendDistributionMetric that ensures the function is defined, and does nothing if it is not.
 * This makes it safe to call this function even if the datadog agent is not installed.
 * @param name - The name of the metric.
 * @param value - The value of the metric.
 * @param tags - The tags of the metric, in the format of `key:value`.
 */
export function sendDistributionMetricSafe(name: string, value: number, ...tags: string[]) {
  try {
    _sendDistributionMetric(name, value, ...tags);
  } catch (e) {
    console.warn(`Error sending DD distribution metric: ${name} with value ${value} and tags ${tags.join(', ')}`, e);
  }
}

let defaultTraceOptions: DatadogTraceOptions = {};

export type DatadogTag = {[k: string]: string | number | DatadogTag};

// More at https://github.com/DataDog/dd-trace-java/blob/master/dd-trace-api/src/main/java/datadog/trace/api/DDSpanTypes.java
type DatadogSpanType = 'web' | 'serverless';

// https://datadoghq.dev/dd-trace-js/modules.html#TraceOptions
type DatadogTraceOptions = { service?: string, resource?: string, type?: DatadogSpanType }

type DatadogTraceFn<T> = (span: DatadogSpan, done: (error?: Error) => void) => T;

export type DatadogSpan = {
  setTag: (key: string, value: string | number | DatadogTag) => void,
  addTags: (tags: DatadogTag) => void
}

export let tracer = new (class {
  /**
   * @deprecated
   * Use the wrapper function `trace` or `traceAsync` instead.
   * (`import { trace, traceAsync } from "@aidkitorg/types/lib/datadog"`)
   * 
   * https://datadoghq.dev/dd-trace-js/interfaces/export_.Tracer.html#trace
   * @param name resource name
   * @param fn 
   */
  trace<T extends DatadogTraceFn<any>>(name: string, fn: T): ReturnType<T>;
  trace<T extends DatadogTraceFn<any>>(name: string, options: DatadogTraceOptions, fn: T): ReturnType<T>;
  trace<T extends DatadogTraceFn<any>>(arg0: string, arg1: T | DatadogTraceOptions, arg2?: T) {
    if (arg2) return arg2({
      setTag: ()=>{},
      addTags: ()=>{},
    }, ()=>{})
    if (typeof arg1 === "function") return arg1({
      setTag: ()=>{},
      addTags: ()=>{},
    }, ()=>{})
  }
  scope() { return ({
    active: (): DatadogSpan | null => null
  })}
  use(plugin: string, config: {
    headers: string[]
  }) {}
})

export function setTypesPackageTracer(_tracer: any, _defaultTraceOptions: DatadogTraceOptions) {
  tracer = _tracer;
  defaultTraceOptions = _defaultTraceOptions
}

let _sendDistributionMetric: (name: string, value: number, ...tags: string[]) => void = () => {};
export function setSendDistributionMetric(sendDistributionMetric: (name: string, value: number, ...tags: string[]) => void) {
  _sendDistributionMetric = sendDistributionMetric;
}
